/**
 * ManualProcessService
 * 
 * Service for managing contract execution processes and procedures.
 * 
 * This service orchestrates the complete workflow for contract-related processes, including:
 * - Form state management and validation
 * - Creation and updating of processes
 * - Document generation
 * - Step navigation and workflow progression
 * - Level configuration management
 * - Documentary review state handling
 * - Process state and loading control
 * 
 * It provides reactive streams for process state, form data, workflow steps, and loading indicators,
 * enabling seamless integration with Angular components and other services.
 */
import { Injectable } from '@angular/core';
import { map, Observable, BehaviorSubject, tap, catchError, switchMap, throwError } from 'rxjs';
import { BodyUpdateProcess, DocumentsRequestBody, FormField, Ids, LecturaIAManual, NormaJuridica, ProcessForm, RequestBody, RespuestaJuridica, SubjectDocumentReview, SubscribeRequestBodyLevels } from '../types/ejecutar-procesos';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Api } from '@core/models/Api.model';
import { ResultResponseInterface } from '@shared/interfaces/result-response.interface';

@Injectable({
  providedIn: 'root'
})
export class ManualProcessService {

  constructor(
    private readonly _http: HttpClient
  ) { }

  private readonly form = new BehaviorSubject<ProcessForm | null>(null);
  form$ = this.form.asObservable();
  
  private readonly levels = new BehaviorSubject<SubscribeRequestBodyLevels>(null);
  levels$ = this.levels.asObservable();
  
  private readonly documentaryReview = new BehaviorSubject<SubjectDocumentReview>(null);
  documentaryReview$ = this.documentaryReview.asObservable();
  
  private readonly nextStep = new BehaviorSubject<number[]|null>(null);
  nextStep$ = this.nextStep.asObservable();

  private readonly loading = new BehaviorSubject<boolean>(false);
  loading$ = this.loading.asObservable();

  /**
   * Set the current process form data in the service.
   * @param form The process form data object.
   */
  setForm(form: ProcessForm): void {
    this.form.next(form);
  }

  /** Formulario en pantalla (trae `query_params` cuando es un enlace externo). */
  get formValue(): ProcessForm | null {
    return this.form.value;
  }

  /** id_actividad_proceso de la actividad en curso, o null en el formulario inicial. */
  get currentStep(): number | null {
    return this.nextStep.value?.[0] ?? null;
  }

  /**
   * Set the levels configuration for the current process.
   * @param levels The levels configuration object.
   */
  setLevels(data: SubscribeRequestBodyLevels): void {
    this.levels.next(data);
  }
  
  /**
   * Set the loading state for process-related operations.
   * @param loading Boolean indicating if a process operation is loading.
   */
  setLoading(loading: boolean): void {
    this.loading.next(loading);
  }
  
  /**
   * Set the next steps in the process workflow.
   * @param nextStep Array of step numbers representing the next steps, or null if none.
   */
  setNextStep(nextStep: number[]|null): void {
    this.nextStep.next(nextStep);
  }
  
  /**
   * Set the current documentary review state and configuration.
   * @param documentaryReview The documentary review state object.
   */
  setDocumentaryReview(documentaryReview: SubjectDocumentReview): void {
    this.documentaryReview.next(documentaryReview);
  }

  /**
   * Concatenate new steps to the current process workflow sequence.
   * Useful for dynamically expanding the workflow during execution.
   * @param nextStep Array of step numbers to add to the current sequence.
   */
  addNextStep(nextStep: number[]): void {
    const currentSteps = this.nextStep.value;
    if (currentSteps && nextStep) {
      const result = currentSteps.concat(nextStep);
      this.nextStep.next(result)
    }
  }

  /**
   * Remove a specific step from the current process workflow sequence.
   * Useful for workflow optimization and cleanup after completing a step.
   * @param nextStep The step number to remove from the sequence.
   */
  deleteFirstNextStep(nextStep: number): void {
    const currentSteps = this.nextStep.value;
    if (currentSteps && nextStep) {
      const result = currentSteps.filter(step => step !== nextStep);
      this.nextStep.next(result);
    }
  }

  /**
   * Retrieve the form configuration for a specific procedure.
   * Updates the form state with the retrieved configuration.
   * @param ids Object containing the process and client identifiers.
   * @param nextStep The next step configuration for the process.
   * @returns Observable emitting the form configuration.
   */
  getProcedureForm(ids: Ids, nextStep: ProcessForm['nextStep'], nombre_cliente: ProcessForm['nombre_cliente']): Observable<any> {
    return this._http.get<ResultResponseInterface<ProcessForm>>(`${Api.BaseUrlContratacion}/procedimiento/${ids.procedimiento_id}/`)
    .pipe(
      tap((resp: ResultResponseInterface<ProcessForm>) => {
        let respForm = resp.objeto;
        this.setForm({
          ...respForm,
          nextStep,
          nombre_cliente
        });
      })
    );
  }
  
  /**
   * Retrieve the form configuration for the next step of a procedure.
   * Updates the form state with the retrieved configuration.
   * @param procedimiento_id The procedure activity ID or string identifier.
   * @param nextStep Configuration for the next step in the process.
   * @param queryParam If true, uses query parameters; otherwise, uses activity process ID.
   * @returns Observable emitting the form configuration response.
   */
  getProcedureFormNextStep(procedimiento_id: Ids['procedimiento_id'] | string, nextStep: ProcessForm['nextStep'], queryParam = false): Observable<ProcessForm> {
    const nameParam = queryParam ? `query_params` : `id_actividad_proceso`;
    const params = { [nameParam]: procedimiento_id };
    return this._http.get<ProcessForm>(`${Api.BaseUrlContratacionAWS}/campo/formulario`, { params: new HttpParams({ fromObject: params }) })
    .pipe(
      tap((resp) => {
        this.setForm({
          ...resp,
          nextStep,
          query_params: queryParam ? procedimiento_id as string : undefined
        });
      }),
    );
  }

  /**
   * Create a new process instance with the specified configuration.
   * @param body Request body containing the process configuration data.
   * @returns Observable emitting the created process details, including ID, process code, registration date, and next steps.
   */
  createProcess(body: RequestBody): Observable<{
    id: number;
    codigo_proceso: string,
    fecha_registro: string,
    nextStep: number[] | null
  }> {
    return this._http.post<ResultResponseInterface<{
      id: number;
      codigo_proceso: string,
      fecha_registro: string,
      nextStep: number[] | null
    }>>(`${Api.BaseUrlContratacion}/procesos/`, body)
      .pipe(
        map(resp => resp.objeto),
        tap(resp => this.setNextStep(resp.nextStep))
      );
  }

  /**
   * Pre-update a process by sending a JSON blob to the specified URL.
   * Used to prepare the process for an update operation.
   * @param url The endpoint URL for the pre-update operation.
   * @param body The Blob containing the update data.
   * @param queryParams Optional query parameters for the request.
   * @returns Observable emitting a string response from the server.
   */
  preUpdateProcess(url: ProcessForm['url'], body: Blob, queryParams: string | null): Observable<string> {
    const params = queryParams ? { query_params: queryParams } : {};
    return this.putBodyConUrlRenovable(url, (urlFirmada) => this._http.put(urlFirmada, body, {
      params: new HttpParams({ fromObject: params }),
      headers: { 'Content-Type': body.type, 'Skip-Authorization': 'true' },
      responseType: 'text'
    }));
  }

  /**
   * Sube el body.json a la URL firmada de S3. Si S3 la rechaza (400/403: la firma venció
   * mientras la página estaba abierta, p. ej. ExpiredToken), pide una URL nueva y reintenta
   * una sola vez. No recarga el formulario: los documentos adjuntos se conservan.
   * Caso: P-26-0000196 CREDIBANCO (6-oct-2026), la candidata veía "No fue posible realizar
   * la solicitud" al enviar.
   */
  private putBodyConUrlRenovable<T>(url: ProcessForm['url'], enviar: (url: string) => Observable<T>): Observable<T> {
    return enviar(url).pipe(
      catchError((err) => {
        if (!(err instanceof HttpErrorResponse) || ![400, 403].includes(err.status)) {
          return throwError(() => err);
        }
        return this.renovarUrlBody().pipe(
          catchError(() => throwError(() => err)),
          switchMap((urlNueva) => enviar(urlNueva)),
        );
      }),
    );
  }

  /**
   * Pide de nuevo el formulario de la actividad en curso solo para obtener una URL de
   * body.json recién firmada; la guarda en el formulario actual sin emitirlo (setForm
   * reconstruiría la pantalla y se perderían los adjuntos).
   */
  private renovarUrlBody(): Observable<string> {
    const form = this.form.value;
    const idActividad = this.nextStep.value?.[0];
    const params = form?.query_params
      ? { query_params: form.query_params }
      : idActividad ? { id_actividad_proceso: String(idActividad) } : null;
    if (!params) {
      return throwError(() => new Error('No hay actividad en curso para renovar la URL'));
    }
    return this._http.get<ProcessForm>(`${Api.BaseUrlContratacionAWS}/campo/formulario`, {
      params: new HttpParams({ fromObject: params })
    }).pipe(
      map((resp) => {
        if (!resp?.url) throw new Error('El formulario no devolvió URL');
        form.url = resp.url;
        return resp.url;
      }),
    );
  }

  /**
   * Update an existing process with new information.
   * @param body Object containing the process update information.
   * @param queryParams Optional query parameters for the update request.
   * @returns Observable emitting the next steps configuration after the update.
   */
  updateProcess(
    { file_name, id_actividad_proceso }: { file_name: ProcessForm['file_name'],
    id_actividad_proceso: BodyUpdateProcess['id_actividad_proceso'] }, 
    queryParams: string | null
  ): Observable<{
    nextStep: number[] | null
  }> {
    const params = queryParams ? { query_params: queryParams } : {};
    
    return this._http.post<ResultResponseInterface<{
      nextStep: number[] | null
    }>>(`${Api.BaseUrlContratacionAWS}/actualizar_proceso`, { file_name }, {
      params: new HttpParams({ fromObject: params })
    }).pipe(
      map(resp => resp.objeto),
      tap(resp => this.handleNextStepUpdate(resp.nextStep, id_actividad_proceso)),
    );
  }

  /**
   * Handle the logic for updating the next step sequence after a process update.
   * Adds new steps and removes the completed activity from the workflow.
   * @param nextStep Array of new steps to be added, or null if no new steps.
   * @param actividadProcesoId ID of the current process activity being updated.
   */
  private handleNextStepUpdate(nextStep: number[] | null, actividadProcesoId: number): void {
    if (nextStep) {
      this.addNextStep(nextStep);
    }
    this.deleteFirstNextStep(actividadProcesoId);
  }

  /**
   * Generate documents based on the provided process configuration and form data.
   * @param body Object containing the process activity ID and form field data.
   * @param queryParams Optional query parameters for document generation.
   * @returns Observable emitting the generated document response data.
   */
  generateDocument(body: {
    id_actividad_proceso: number;
    datos: Record<FormField['id'], any>;
  }, queryParams: string | null): Observable<DocumentsRequestBody[]> {
    const params = queryParams ? { query_params: queryParams } : {};
    const headers = queryParams ? { 'Skip-Authorization': 'true' } : {};
    return this._http.post<ResultResponseInterface<DocumentsRequestBody[]>>(`${Api.BaseUrlContratacion}/generar-documentos/`, body, {
      params: new HttpParams({ fromObject: params }),
      headers
    }).pipe(map((resp) => resp.objeto));
  }

  /**
   * Botón "Leer con IA": lee con IA los documentos configurados en la actividad y devuelve
   * la propuesta (veredicto, motivo y datos por documento). No guarda nada en el proceso.
   * @param id_actividad_proceso Actividad del proceso que se está revisando.
   */
  /**
   * Actividad "Respuesta con BICHO": estado del caso (análisis, preguntas, chat, respuesta sugerida y plazo).
   */
  getRespuestaJuridica(id_actividad_proceso: number): Observable<RespuestaJuridica> {
    return this._http.get<ResultResponseInterface<RespuestaJuridica>>(`${Api.BaseUrlContratacionAWS}/respuesta-juridica`, {
      params: new HttpParams({ fromObject: { id_actividad_proceso } })
    }).pipe(map((resp) => resp.objeto));
  }

  /** Texto oficial de un artículo citado por BICHO (ej. 'CST|65'). */
  getNormaJuridica(id_actividad_proceso: number, norma: string): Observable<NormaJuridica> {
    return this._http.get<ResultResponseInterface<NormaJuridica>>(`${Api.BaseUrlContratacionAWS}/respuesta-juridica`, {
      params: new HttpParams({ fromObject: { id_actividad_proceso, norma } })
    }).pipe(map((resp) => resp.objeto));
  }

  /**
   * Acciones de la respuesta jurídica: respuestas | soporte | chat | borrador | analizar.
   * chat, borrador y analizar quedan corriendo: el estado vuelve con `trabajando` y se consulta de nuevo.
   */
  accionRespuestaJuridica(id_actividad_proceso: number, accion: string, datos: Record<string, unknown> = {}): Observable<RespuestaJuridica> {
    return this._http.post<ResultResponseInterface<RespuestaJuridica>>(`${Api.BaseUrlContratacionAWS}/respuesta-juridica`, {
      id_actividad_proceso, accion, ...datos
    }).pipe(map((resp) => resp.objeto));
  }

  leerConIA(id_actividad_proceso: number): Observable<LecturaIAManual> {
    return this._http.post<ResultResponseInterface<LecturaIAManual>>(`${Api.BaseUrlContratacion}/lectura-ia/`, { id_actividad_proceso })
      .pipe(map((resp) => resp.objeto));
  }

  /**
   * Save process data to a specified URL endpoint.
   * Performs a PUT request to persist process form data with optional query parameters.
   * This method is typically used to save form state during process execution or to 
   * update existing process data without changing the workflow state.
   * 
   * @param url The target URL endpoint where the process data should be saved.
   * @param body Object containing key-value pairs of form data to be saved.
   * @param queryParams Optional query parameters to be included in the request, or null if none.
   * @returns Observable emitting the server response after the save operation.
   */
  saveProcess(url: ProcessForm['url'], body: {[key: string]: string}, queryParams: string | null): Observable<any> {
    const params = queryParams ? { query_params: queryParams } : {};
    return this.putBodyConUrlRenovable(url, (urlFirmada) => this._http.put<ResultResponseInterface<any>>(urlFirmada, {datos: body}, {
      params: new HttpParams({ fromObject: params }),
      headers: { 'Skip-Authorization': 'true' }
    }));
  }

  /**
   * Retrieve query parameters from a shortened URL using the provided UUID.
   * This method performs a GET request to fetch the original query parameters
   * associated with the given UUID, which is typically used for accessing
   * specific process forms or workflows.
   * 
   * @param uuid The unique identifier used to retrieve the associated query parameters.
   * @returns Observable emitting an object containing the query string.
   */ 
  getQueryParams(uuid: string): Observable<{query_string: string}> {
    return this._http.get<{query_string: string}>(`${Api.BaseUrlEnlaceAcortadoAWS}/${uuid}`, {
      headers: { 'Skip-Authorization': 'true' }
    })
  }
}
